import AccountTransactions from "@components/AccountNew/AccountTransactions/AccountTransactions"
import TabsView from "@components/TabsView"
import PossibleAccountTransactions from "@components/AccountNew/AccountTransactions/PossibleAccountTransactions"
import { AccountInfoSidebar } from "@components/AccountNew/AccountInfoSidebar/AccountInfoSidebar"
import { Invoices } from "@components/AccountNew/Invoices/Invoices"
import { EventsTimeline } from "@components/EventsTimeline/EventsTimeline"
import { AccountNotifications } from "@components/AccountNew/AccountNotifications/AccountNotifications"
import { OffersFunnel } from "@components/AccountNew/OfferFunnel/OffersFunnel"
import { AccountRewards } from "@components/AccountNew/Rewards/AccountRewards"
import { useAccountDropInfoSerializer } from "@components/AccountNew/AccountInfoSidebar/utils/accountDropSerializerSchema"
import { useAccount } from "@apis/accounts/useAccounts"
import {
  showDropInformation,
  showRejectInformation,
} from "@apis/accounts/utils"
import { CardDataList } from "@components/CardDataList/CardDataList"
import { TriangleAlert } from "lucide-react"
import { RejectInformation } from "@components/AccountNew/RejectInformationForm/RejectInformationForm"
import { CancelAccount } from "@components/AccountNew/CancelAccount/CancelAccount"
import { canCancelAccount } from "./utils/helper"
import { WeatherWidget } from "@components/WeatherWidget"

const AccountTransactionsTab = ({ account_id }: { account_id: string }) => (
  <div className="tw:flex tw:flex-col tw:gap-8">
    <AccountTransactions account_id={account_id} />
    <PossibleAccountTransactions account_id={account_id} />
  </div>
)

const AccountSummaryTab = ({
  account_id,
  prospect_guid,
}: {
  account_id: string
  prospect_guid: string
}) => {
  const useAccountQuery = useAccount(account_id)
  const { data: accountDropInfoData, isError: accountDropInfoIsError } =
    useAccountDropInfoSerializer(account_id)

  const showDropInfo =
    accountDropInfoData &&
    showDropInformation(
      accountDropInfoData.status_group,
      accountDropInfoData.account_status_code,
    )

  const showRejectInfo = showRejectInformation(accountDropInfoData.status_group)

  return (
    <div className="tw:flex tw:w-full tw:gap-4">
      <div className="tw:w-full tw:max-w-[315px] tw:flex-shrink-0 tw:md:w-1/4">
        <AccountInfoSidebar account_id={account_id} />
      </div>

      <div className="tw:grid tw:flex-1 tw:grid-cols-2 tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-4">
          {showRejectInfo && (
            <div className="tw:bordered-container">
              <RejectInformation account_id={account_id} />
            </div>
          )}
          <div className="tw:flex tw:flex-col tw:gap-3">
            <div className="tw:bordered-container">
              <Invoices account_id={account_id} condensed={true} />
            </div>
            <OffersFunnel account_id={account_id} />
            {canCancelAccount(useAccountQuery.data?.status_group) && (
              <div className="tw:bordered-container">
                <CancelAccount account_id={account_id} />
              </div>
            )}
          </div>
        </div>
        <div className="tw:flex tw:flex-col tw:gap-3">
          {showDropInfo && (
            <CardDataList
              items={accountDropInfoData.items}
              isError={accountDropInfoIsError}
            >
              <CardDataList.Title>
                <div className="tw:flex tw:gap-2 tw:text-destructive">
                  <span>Drop Information</span>
                  <TriangleAlert />
                </div>
              </CardDataList.Title>
            </CardDataList>
          )}
          <div className="tw:bordered-container tw:px-4 tw:pt-4">
            <AccountNotifications
              prospect_guid={prospect_guid}
              account_id={account_id}
            />
          </div>
          <div className="tw:bordered-container tw:px-4 tw:pt-4">
            <EventsTimeline
              prospect_guid={prospect_guid}
              account_id={account_id}
              eventType="account"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const InvoicesTab = ({ account_id }: { account_id: string }) => (
  <div className="tw:flex tw:w-full tw:gap-4">
    <div className="tw:w-full tw:max-w-[315px] tw:flex-shrink-0 tw:md:w-1/4">
      <AccountInfoSidebar account_id={account_id} />
    </div>
    <div className="tw:bordered-container tw:flex-1">
      <Invoices account_id={account_id} condensed={false} />
    </div>
  </div>
)

export const AccountTabs = ({
  account_id,
  prospect_guid,
}: {
  account_id: string
  prospect_guid: string | undefined
}) => {
  const tabs = [
    {
      name: "Summary",
      value: "summary",
      content: (
        <AccountSummaryTab
          account_id={account_id}
          prospect_guid={prospect_guid as string}
        />
      ),
    },
    {
      name: "Rewards",
      value: "rewards",
      content: <AccountRewards account_id={account_id} />,
    },
    {
      name: "Invoices",
      value: "invoices",
      content: <InvoicesTab account_id={account_id} />,
    },
    {
      name: "Account Transactions",
      value: "transactions",
      content: <AccountTransactionsTab account_id={account_id} />,
    },
    {
      name: "Weather",
      value: "weather",
      content: null,
      displayOnly: <WeatherWidget account_id={account_id} />,
    },
  ]
  return <TabsView tabValues={tabs} />
}
